import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyInfo } from '../api/authApi';
import { getMyOrders, getMyAddresses, addAddress, setDefaultAddress, deleteAddress } from '../api/myPageApi';
import { removeToken, isLoggedIn } from '../utils/tokenUtils';
import styles from './MyPage.module.css';
import global from '../styles/global.module.css';
import { formatPhone } from '../utils/phoneUtils';
import { useModal } from '../context/ModalContext';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const statusLabels = {
    PENDING: '결제 대기', PAID: '결제 완료',
    SHIPPING: '배송 중', DELIVERED: '배송 완료', CANCELLED: '취소'
};

const getTrackingUrl = (courierName, trackingNumber) => {
    const urls = {
        'CJ대한통운': `https://trace.cjlogistics.com/tracking?wblNo=${trackingNumber}`,
        '롯데택배': `https://www.lotteglogis.com/home/reservation/tracking/index?InvNo=${trackingNumber}`,
        '한진택배': `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mCode=MN038&schLang=KR&wblnumText2=${trackingNumber}`,
        '우체국택배': `https://service.epost.go.kr/trace.RetrieveDomRigiTraceList.comm?sid1=${trackingNumber}`,
        '로젠택배': `https://www.ilogen.com/web/personal/trace/${trackingNumber}`,
    };
    return urls[courierName] || null;
};

function DeliverySteps({ status }) {
    const steps = [
        { key: 'PAID', label: '결제 완료', icon: '💳' },
        { key: 'SHIPPING', label: '배송 중', icon: '🚚' },
        { key: 'DELIVERED', label: '배송 완료', icon: '📦' },
    ];
    const order = ['PAID', 'SHIPPING', 'DELIVERED'];
    const currentIndex = order.indexOf(status);

    return (
        <div className={styles.deliverySteps}>
            {steps.map((step, i) => (
                <div key={step.key} className={styles.deliveryStepWrapper}>
                    <div className={`${styles.deliveryStep} ${i <= currentIndex ? styles.deliveryStepActive : ''}`}>
                        <div className={`${styles.deliveryStepCircle} ${i <= currentIndex ? styles.deliveryStepCircleActive : ''} ${i === currentIndex ? styles.deliveryStepCircleCurrent : ''}`}>
                            {i < currentIndex ? '✓' : step.icon}
                        </div>
                        <span className={`${styles.deliveryStepLabel} ${i === currentIndex ? styles.deliveryStepLabelCurrent : ''}`}>
                            {step.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`${styles.deliveryStepLine} ${i < currentIndex ? styles.deliveryStepLineActive : ''}`} />
                    )}
                </div>
            ))}
        </div>
    );
}

function MyPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('info');
    const [userInfo, setUserInfo] = useState(null);

    useEffect(() => {
        if (!isLoggedIn()) { navigate('/login'); return; }
        loadUserInfo();
    }, []);

    const loadUserInfo = async () => {
        try {
            const response = await getMyInfo();
            setUserInfo(response.data);
        } catch { navigate('/login'); }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>MY PAGE</h2>
            <div className={styles.tabs}>
                {[
                    { key: 'info', label: '내 정보' },
                    { key: 'orders', label: '주문내역' },
                    { key: 'addresses', label: '배송지 관리' },
                    { key: 'reviews', label: '리뷰 내역' },
                    { key: 'password', label: '비밀번호 변경' },
                    { key: 'delete', label: '회원탈퇴' }
                ].map(tab => (
                    <button key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab(tab.key)}>
                        {tab.label}
                    </button>
                ))}
            </div>
            {activeTab === 'info' && <InfoTab userInfo={userInfo} onUpdate={loadUserInfo} />}
            {activeTab === 'orders' && <OrdersTab navigate={navigate} />}
            {activeTab === 'addresses' && <AddressesTab />}
            {activeTab === 'reviews' && <ReviewsTab />}
            {activeTab === 'password' && <PasswordTab />}
            {activeTab === 'delete' && <DeleteTab />}
        </div>
    );
}

/* =====================
   내 정보 탭
===================== */
function InfoTab({ userInfo, onUpdate }) {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({ name: '', phone: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (userInfo) setForm({ name: userInfo.name, phone: userInfo.phone || '' });
    }, [userInfo]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        try {
            const { updateUser } = await import('../api/userApi');
            await updateUser(form);
            setSuccess('정보가 수정되었습니다.');
            setEditing(false);
            onUpdate();
        } catch { setError('수정에 실패했습니다.'); }
    };

    return (
        <div>
            <p className={styles.sectionTitle}>기본 정보</p>
            {!editing ? (
                <div>
                    <div className={styles.infoRow}><span className={styles.infoLabel}>이름</span><span className={styles.infoValue}>{userInfo?.name}</span></div>
                    <div className={styles.infoRow}><span className={styles.infoLabel}>이메일</span><span className={styles.infoValue}>{userInfo?.email}</span></div>
                    <div className={styles.infoRow}><span className={styles.infoLabel}>전화번호</span><span className={styles.infoValue}>{userInfo?.phone || '-'}</span></div>
                    <div className={styles.buttonGroup}>
                        <button className={global.btnPrimary} onClick={() => setEditing(true)}>수정하기</button>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    {error && <p className={global.errorText}>{error}</p>}
                    {success && <p className={global.successText}>{success}</p>}
                    <div className={styles.formRow}>
                        <label className={styles.label}>이름</label>
                        <input type="text" name="name" value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                            className={global.input} />
                    </div>
                    <div className={styles.formRow}>
                        <label className={styles.label}>전화번호</label>
                        <input type="tel" value={form.phone}
                            onChange={e => setForm({ ...form, phone: formatPhone(e.target.value) })}
                            className={global.input} placeholder="010-0000-0000" maxLength={13} />
                    </div>
                    <div className={styles.buttonGroup}>
                        <button type="submit" className={global.btnPrimary}>저장</button>
                        <button type="button" className={global.btnOutline} onClick={() => setEditing(false)}>취소</button>
                    </div>
                </form>
            )}
        </div>
    );
}

/* =====================
   주문내역 탭
===================== */
function OrdersTab({ navigate }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [periodFilter, setPeriodFilter] = useState('all');
    const { showAlert, showConfirm } = useModal();

    useEffect(() => { loadOrders(); }, []);

    const loadOrders = async () => {
        try {
            const res = await getMyOrders();
            setOrders(res.data);
        } catch { console.error('주문내역 로딩 실패'); }
        finally { setLoading(false); }
    };

    const handleCancel = async (e, orderId) => {
        e.stopPropagation();
        const ok = await showConfirm('주문을 취소하시겠습니까?');
        if (!ok) return;
        try {
            const { cancelOrder: cancel } = await import('../api/orderApi');
            await cancel(orderId);
            setOrders(prev => prev.map(o =>
                o.orderId === orderId ? { ...o, status: 'CANCELLED' } : o
            ));
            await showAlert('주문이 취소되었습니다.', 'success');
        } catch {
            await showAlert('주문 취소에 실패했습니다.', 'error');
        }
    };

    const filteredOrders = orders.filter(order => {
        if (periodFilter === 'all') return true;
        const now = new Date();
        const orderDate = new Date(order.createdAt);
        if (periodFilter === '1m') return orderDate >= new Date(new Date().setMonth(now.getMonth() - 1));
        if (periodFilter === '3m') return orderDate >= new Date(new Date().setMonth(now.getMonth() - 3));
        if (periodFilter === '6m') return orderDate >= new Date(new Date().setMonth(now.getMonth() - 6));
        if (periodFilter === '1y') return orderDate >= new Date(new Date().setFullYear(now.getFullYear() - 1));
        return true;
    });

    if (loading) return <p className={styles.loading}>로딩 중...</p>;

    return (
        <div>
            <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>주문내역</p>
                <div className={styles.periodFilter}>
                    {[
                        { key: 'all', label: '전체' },
                        { key: '1m', label: '1개월' },
                        { key: '3m', label: '3개월' },
                        { key: '6m', label: '6개월' },
                        { key: '1y', label: '1년' }
                    ].map(p => (
                        <button key={p.key}
                            className={`${styles.filterBtn} ${periodFilter === p.key ? styles.filterBtnActive : ''}`}
                            onClick={() => setPeriodFilter(p.key)}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {filteredOrders.length === 0 ? (
                <div className={styles.empty}>
                    <p>주문내역이 없습니다.</p>
                    <button className={global.btnPrimary} onClick={() => navigate('/products')}>쇼핑하러 가기</button>
                </div>
            ) : (
                <div className={styles.orderList}>
                    {filteredOrders.map(order => (
                        <div key={order.orderId} className={styles.orderCard}>
                            <div className={styles.orderHeader}
                                onClick={() => setExpandedOrder(expandedOrder === order.orderId ? null : order.orderId)}>
                                <div className={styles.orderMeta}>
                                    <span className={styles.orderDate}>{new Date(order.createdAt).toLocaleDateString()}</span>
                                    <span className={styles.orderId}>주문번호: {order.orderId}</span>
                                </div>
                                <div className={styles.orderRight}>
                                    <span className={styles[`orderStatus_${order.status}`]}>{statusLabels[order.status]}</span>
                                    <span className={styles.orderTotal}>{order.totalPrice?.toLocaleString()}원</span>
                                    {(order.status === 'PENDING' || order.status === 'PAID') && (
                                        <button className={styles.cancelBtn}
                                            onClick={(e) => handleCancel(e, order.orderId)}>
                                            주문 취소
                                        </button>
                                    )}
                                    <span className={styles.expandIcon}>{expandedOrder === order.orderId ? '▲' : '▼'}</span>
                                </div>
                            </div>

                            {expandedOrder === order.orderId && (
                                <div className={styles.orderItems}>
                                    {order.items?.map((item, i) => (
                                        <div key={i} className={styles.orderItem}>
                                            <img src={`${API_URL}${item.imageUrl}`}
                                                alt={item.productName} className={styles.orderItemImage} />
                                            <div className={styles.orderItemInfo}>
                                                <p className={styles.orderItemName}>{item.productName}</p>
                                                <p className={styles.orderItemDetail}>{item.quantity}개 · {item.price?.toLocaleString()}원</p>
                                            </div>
                                        </div>
                                    ))}
                                    {order.trackingNumber && (
                                        <div className={styles.trackingInfo}>
                                            <span className={styles.trackingLabel}>배송 조회</span>
                                            {getTrackingUrl(order.courierName, order.trackingNumber) ? (
                                                <a href={getTrackingUrl(order.courierName, order.trackingNumber)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className={styles.trackingLink}>
                                                    {order.courierName} {order.trackingNumber}
                                                </a>
                                            ) : (
                                                <span className={styles.trackingNumber}>{order.courierName} {order.trackingNumber}</span>
                                            )}
                                        </div>
                                    )}
                                    {(order.status === 'PAID' || order.status === 'SHIPPING' || order.status === 'DELIVERED') && (
                                        <DeliverySteps status={order.status} />
                                    )}
                                    <div className={styles.orderSummary}>
                                        <div className={styles.orderSummaryRow}>
                                            <span>상품 금액</span>
                                            <span>{(order.totalPrice - (order.deliveryFee || 0)).toLocaleString()}원</span>
                                        </div>
                                        <div className={styles.orderSummaryRow}>
                                            <span>배송비</span>
                                            <span>{order.deliveryFee > 0 ? `${order.deliveryFee.toLocaleString()}원` : '무료'}</span>
                                        </div>
                                        <div className={`${styles.orderSummaryRow} ${styles.orderSummaryTotal}`}>
                                            <span>총 결제금액</span>
                                            <span>{order.totalPrice?.toLocaleString()}원</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* =====================
   배송지 관리 탭
===================== */
function AddressesTab() {
    const [addresses, setAddresses] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ recipient: '', phone: '', zipcode: '', address1: '', address2: '' });
    const [loading, setLoading] = useState(true);
    const { showAlert, showConfirm } = useModal();

    useEffect(() => { loadAddresses(); }, []);

    const loadAddresses = async () => {
        try {
            const res = await getMyAddresses();
            setAddresses(res.data);
        } catch { console.error('배송지 로딩 실패'); }
        finally { setLoading(false); }
    };

    const handleSearchAddress = () => {
        new window.daum.Postcode({
            oncomplete: (data) => {
                setForm(prev => ({ ...prev, zipcode: data.zonecode, address1: data.roadAddress }));
            }
        }).open();
    };

    const handleAddAddress = async () => {
        if (!form.recipient || !form.phone || !form.zipcode || !form.address1) {
            await showAlert('필수 항목을 입력해주세요.', 'warning'); return;
        }
        try {
            const res = await addAddress(form);
            setAddresses(prev => [...prev, res.data]);
            setForm({ recipient: '', phone: '', zipcode: '', address1: '', address2: '' });
            setShowForm(false);
            await showAlert('배송지가 추가되었습니다.', 'success');
        } catch { await showAlert('배송지 추가에 실패했습니다.', 'error'); }
    };

    const handleSetDefault = async (addressId) => {
        try {
            await setDefaultAddress(addressId);
            setAddresses(prev => prev.map(a => ({ ...a, isDefault: a.addressId === addressId })));
        } catch { await showAlert('기본 배송지 설정에 실패했습니다.', 'error'); }
    };

    const handleDelete = async (addressId) => {
        const ok = await showConfirm('배송지를 삭제하시겠습니까?');
        if (!ok) return;
        try {
            await deleteAddress(addressId);
            setAddresses(prev => prev.filter(a => a.addressId !== addressId));
        } catch { await showAlert('삭제에 실패했습니다.', 'error'); }
    };

    if (loading) return <p className={styles.loading}>로딩 중...</p>;

    return (
        <div>
            <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>배송지 관리</p>
                <button className={global.btnPrimary} onClick={() => setShowForm(!showForm)}>+ 배송지 추가</button>
            </div>
            {showForm && (
                <div className={styles.addressForm}>
                    <div className={styles.formRow}>
                        <label className={styles.label}>받는 분 *</label>
                        <input type="text" value={form.recipient}
                            onChange={e => setForm({ ...form, recipient: e.target.value })}
                            className={global.input} placeholder="받는 분 이름" />
                    </div>
                    <div className={styles.formRow}>
                        <label className={styles.label}>연락처 *</label>
                        <input type="tel" value={form.phone}
                            onChange={e => setForm({ ...form, phone: formatPhone(e.target.value) })}
                            className={global.input} placeholder="010-0000-0000" maxLength={13} />
                    </div>
                    <div className={styles.formRow}>
                        <label className={styles.label}>주소 *</label>
                        <div className={styles.addressSearch}>
                            <input type="text" value={form.zipcode} readOnly
                                className={global.input} placeholder="우편번호" />
                            <button type="button" className={global.btnOutline}
                                onClick={handleSearchAddress}>주소 검색</button>
                        </div>
                        <input type="text" value={form.address1} readOnly
                            className={`${global.input} ${styles.addressInput}`} placeholder="기본 주소" />
                        <input type="text" value={form.address2}
                            onChange={e => setForm({ ...form, address2: e.target.value })}
                            className={`${global.input} ${styles.addressInput}`} placeholder="상세 주소" />
                    </div>
                    <div className={styles.buttonGroup}>
                        <button className={global.btnPrimary} onClick={handleAddAddress}>저장</button>
                        <button className={global.btnOutline} onClick={() => setShowForm(false)}>취소</button>
                    </div>
                </div>
            )}
            {addresses.length === 0 ? (
                <p className={styles.empty}>등록된 배송지가 없습니다.</p>
            ) : (
                <div className={styles.addressList}>
                    {addresses.map(addr => (
                        <div key={addr.addressId}
                            className={`${styles.addressCard} ${addr.isDefault ? styles.defaultCard : ''}`}>
                            <div className={styles.addressInfo}>
                                {addr.isDefault && <span className={styles.defaultBadge}>기본 배송지</span>}
                                <p className={styles.addressRecipient}>{addr.recipient} · {addr.phone}</p>
                                <p className={styles.addressText}>[{addr.zipcode}] {addr.address1} {addr.address2}</p>
                            </div>
                            <div className={styles.addressActions}>
                                {!addr.isDefault && (
                                    <button className={global.btnOutline}
                                        onClick={() => handleSetDefault(addr.addressId)}>기본 설정</button>
                                )}
                                <button className={styles.btnDelete}
                                    onClick={() => handleDelete(addr.addressId)}>삭제</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* =====================
   리뷰 내역 탭
===================== */
function ReviewsTab() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const { showAlert, showConfirm } = useModal();

    useEffect(() => { loadReviews(); }, []);

    const loadReviews = async () => {
        try {
            const { getMyReviews } = await import('../api/reviewApi');
            const res = await getMyReviews();
            setReviews(res.data);
        } catch { console.error('리뷰 로딩 실패'); }
        finally { setLoading(false); }
    };

    const handleDelete = async (reviewId) => {
        const ok = await showConfirm('리뷰를 삭제하시겠습니까?');
        if (!ok) return;
        try {
            const { deleteReview } = await import('../api/reviewApi');
            await deleteReview(reviewId);
            setReviews(prev => prev.filter(r => r.reviewId !== reviewId));
        } catch { await showAlert('삭제에 실패했습니다.', 'error'); }
    };

    if (loading) return <p className={styles.loading}>로딩 중...</p>;

    return (
        <div>
            <p className={styles.sectionTitle}>리뷰 내역</p>
            {reviews.length === 0 ? (
                <p className={styles.empty}>작성한 리뷰가 없습니다.</p>
            ) : (
                <div className={styles.reviewList}>
                    {reviews.map(review => (
                        <div key={review.reviewId} className={styles.reviewCard}>
                            <div className={styles.reviewCardHeader}>
                                <div className={styles.reviewCardInfo}>
                                    <span className={styles.reviewProductName}>{review.productName}</span>
                                    <span className={styles.reviewRating}>♥ {review.rating}.0</span>
                                    <span className={styles.reviewDate}>
                                        {new Date(review.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                                <button className={styles.btnDelete}
                                    onClick={() => handleDelete(review.reviewId)}>삭제</button>
                            </div>
                            <p className={styles.reviewContent}>{review.content}</p>
                            {review.imageUrl && (
                                <img src={`${API_URL}${review.imageUrl}`}
                                    alt="리뷰 이미지" className={styles.reviewImage} />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/* =====================
   비밀번호 변경 탭
===================== */
function PasswordTab() {
    const [form, setForm] = useState({ currentPassword: '', newPassword: '', newPasswordConfirm: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess('');
        if (form.newPassword !== form.newPasswordConfirm) {
            setError('새 비밀번호가 일치하지 않습니다.'); return;
        }
        try {
            const { updatePassword } = await import('../api/userApi');
            await updatePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
            setSuccess('비밀번호가 변경되었습니다.');
            setForm({ currentPassword: '', newPassword: '', newPasswordConfirm: '' });
        } catch { setError('현재 비밀번호가 올바르지 않습니다.'); }
    };

    return (
        <form onSubmit={handleSubmit}>
            <p className={styles.sectionTitle}>비밀번호 변경</p>
            {error && <p className={global.errorText}>{error}</p>}
            {success && <p className={global.successText}>{success}</p>}
            <div className={styles.formRow}>
                <label className={styles.label}>현재 비밀번호</label>
                <input type="password" value={form.currentPassword}
                    onChange={e => setForm({ ...form, currentPassword: e.target.value })}
                    className={global.input} required />
            </div>
            <div className={styles.formRow}>
                <label className={styles.label}>새 비밀번호</label>
                <input type="password" value={form.newPassword}
                    onChange={e => setForm({ ...form, newPassword: e.target.value })}
                    className={global.input} required />
            </div>
            <div className={styles.formRow}>
                <label className={styles.label}>새 비밀번호 확인</label>
                <input type="password" value={form.newPasswordConfirm}
                    onChange={e => setForm({ ...form, newPasswordConfirm: e.target.value })}
                    className={global.input} required />
            </div>
            <div className={styles.buttonGroup}>
                <button type="submit" className={global.btnPrimary}>변경하기</button>
            </div>
        </form>
    );
}

/* =====================
   회원탈퇴 탭
===================== */
function DeleteTab() {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const { showConfirm } = useModal();

    const handleDelete = async (e) => {
        e.preventDefault();
        setError('');
        const ok = await showConfirm('정말 탈퇴하시겠습니까?\n탈퇴 시 모든 정보가 삭제됩니다.');
        if (!ok) return;
        try {
            const { deleteUser } = await import('../api/userApi');
            await deleteUser({ password });
            removeToken();
            navigate('/');
        } catch { setError('비밀번호가 올바르지 않습니다.'); }
    };

    return (
        <form onSubmit={handleDelete}>
            <p className={styles.sectionTitle}>회원탈퇴</p>
            {error && <p className={global.errorText}>{error}</p>}
            <div className={styles.infoRow}>
                <span className={styles.infoLabel}>안내</span>
                <span className={styles.infoValue}>탈퇴 시 모든 정보가 삭제되며 복구할 수 없습니다.</span>
            </div>
            <div className={styles.formRow} style={{ marginTop: '24px' }}>
                <label className={styles.label}>비밀번호 확인</label>
                <input type="password" value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={global.input} placeholder="현재 비밀번호 입력" required />
            </div>
            <div className={styles.buttonGroup}>
                <button type="submit" className={styles.btnDanger}>탈퇴하기</button>
            </div>
        </form>
    );
}

export default MyPage;